import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TranscriptsView } from '@/enums';

/**
 * Prefix for transcript filter URL params. Uses a single fixed value
 * (rather than the per-view TableType) so filters persist when the user
 * toggles between Completed / Incomplete / Screened-out tabs.
 */
export const TRANSCRIPTS_FILTER_PREFIX = 'transcripts_filter_';

export const getTranscriptFilterParamName = (columnId: string): string =>
  `${TRANSCRIPTS_FILTER_PREFIX}${columnId}`;

/**
 * Whitelist of filterable non-metadata column IDs. Metadata.* columns are
 * filterable unless excluded by METADATA_EXCLUSION_LIST.
 */
const FIXED_FILTERABLE_COLUMN_IDS = new Set(['participants', 'conversation_id']);

/**
 * Metadata keys that are always hidden from both the filter picker AND the
 * default column visibility. These are Tellet-internal or panel-integration
 * fields that are never useful to filter on, and clutter the table UI by
 * default. Users can still toggle column visibility on via the View menu.
 *
 * Comparison is case-insensitive — store each entry lowercase.
 * Keep this list deliberately short; only add entries when data shows them
 * to be universally low-value, never on name-pattern guessing.
 */
const METADATA_EXCLUSION_LIST_LOWER: ReadonlySet<string> = new Set([
  'computed_quality',
  'skipconsent',
  'source',
  'screeningstatus',
  'completioncode',
]);

// TELV1-591 — randomisation order keys (`order: …`, `position: …`) are one
// column per randomised child; hidden by default so a 30-question randomised
// guide does not add 30 columns. Still available via the View menu, the
// single-transcript metadata panel, filters and the export.
const RANDOMISATION_ORDER_PREFIXES = ['order: ', 'position: '];

export const isExcludedMetadataKey = (key: string): boolean =>
  METADATA_EXCLUSION_LIST_LOWER.has(key.toLowerCase()) ||
  RANDOMISATION_ORDER_PREFIXES.some((prefix) => key.startsWith(prefix));

export const isFilterableColumnId = (columnId: string): boolean => {
  if (FIXED_FILTERABLE_COLUMN_IDS.has(columnId)) return true;
  if (!columnId.startsWith('metadata.')) return false;
  const key = columnId.slice('metadata.'.length);
  return !isExcludedMetadataKey(key);
};

/**
 * Map frontend column IDs to the backend field names the API accepts.
 * Only `participants` needs translation. Shared with sorting so there's
 * one source of truth.
 */
export const COLUMN_TO_BACKEND_FIELD: Record<string, string> = {
  participants: 'respondent_index',
};

/**
 * Human-readable label for a metadata key — converts `snake_case` or
 * `camelCase` to space-separated Title Case. Used by both the picker
 * and the table header.
 */
export const formatColumnLabel = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Display label for any TanStack Table column ID on the transcripts
 * table. Used by the View (visibility) menu so users see "Age" rather
 * than "metadata.age" and "Summary" rather than "digest.summary".
 */
export const getColumnDisplayLabel = (columnId: string): string => {
  if (columnId === 'participants') return 'Participant #';
  if (columnId === 'conversation_id') return 'Conversation ID';
  if (columnId === 'startedAt') return 'Start time';
  if (columnId === 'duration') return 'Duration';
  if (columnId === 'digest.summary') return 'Summary';
  if (columnId.startsWith('metadata.')) {
    return formatColumnLabel(columnId.slice('metadata.'.length));
  }
  return formatColumnLabel(columnId);
};

export interface FilterableColumn {
  id: string;
  label: string;
}

/**
 * Catalog of columns the user can pick to filter on. Derived from the
 * project's actual metadata keys — never from a hardcoded list of
 * substantive fields.
 *
 * `activeView` omits the `participants` entry for Screened Out: those rows
 * have no `respondent_index` (they never entered the interview), so a
 * Participant # filter would always resolve to zero results there.
 */
export const getFilterableColumnsCatalog = (
  metadataKeys: string[],
  activeView?: TranscriptsView,
): FilterableColumn[] => {
  // Some projects have case-variant metadata keys from different ingestion
  // sources (e.g. "Language" from a panel CSV + "language" auto-added by the
  // platform). Both would render with the same display label via
  // formatColumnLabel, producing duplicate-looking pills. Collapse to the
  // first occurrence per lowercased key.
  const seen = new Set<string>();
  const dedupedMetadata = metadataKeys
    .filter((key) => !isExcludedMetadataKey(key))
    .filter((key) => {
      const lower = key.toLowerCase();
      if (seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
  return [
    ...(activeView === TranscriptsView.SCREENED_OUT
      ? []
      : [{ id: 'participants', label: 'Participant #' }]),
    { id: 'conversation_id', label: 'Conversation ID' },
    ...dedupedMetadata.map((key) => ({
      id: `metadata.${key}`,
      label: formatColumnLabel(key),
    })),
  ];
};

/**
 * Reads per-column transcript filters from the URL and returns them
 * keyed by backend field name (ready to spread into the API call).
 *
 * `activeView` drops the `participants` filter (respondent_index) for
 * Screened Out: those rows have no respondent_index, so a Participant #
 * pill set while browsing Completed would otherwise silently zero out
 * the Screened Out table when the user switches tabs.
 */
export const useTranscriptsFilters = (
  activeView?: TranscriptsView,
): Record<string, string> | undefined => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const out: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      if (!key.startsWith(TRANSCRIPTS_FILTER_PREFIX)) return;
      const columnId = key.slice(TRANSCRIPTS_FILTER_PREFIX.length);
      if (!isFilterableColumnId(columnId)) return;
      if (activeView === TranscriptsView.SCREENED_OUT && columnId === 'participants') return;
      const trimmed = value.trim();
      if (!trimmed) return;
      const backendKey = COLUMN_TO_BACKEND_FIELD[columnId] ?? columnId;
      out[backendKey] = trimmed;
    });

    return Object.keys(out).length ? out : undefined;
  }, [searchParams, activeView]);
};
